import { array, number, option, ord, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';

import { getOrFalse } from '@mews/fp';
import { composeProps } from '@mews/framework/redux';

import { getDefaultAdultCount, getDefaultChildCount } from '../../../modules/configuration/selectors';
import { getUniqueAgeCategoriesPerClassification } from '../../../modules/entities/ageCategories/selectors';
import { getEnterpriseServiceId } from '../../../modules/entities/services/selectors';
import {
    categoryEnterpriseIdPropsExtender,
    getCategoriesMeta,
    getCategoryMaxPersonCount,
} from '../../../modules/resourceCategories/selectors';
import { OccupancyData } from '../../../modules/resourceOrder/occupancyTypes';
import { ApplicationState } from '../../../types';
import { SPACE_CATEGORIES_VIEW_SLICE_NAME } from '../../../views/spaceCategories/reducer';
import { parentSelector } from '../../parentSelector';
import { getSelectedCityServiceIds } from '../selectedCityServices';

export const getCategoryServiceId = composeProps(
    (state, { enterpriseId }) => (enterpriseId != null ? getEnterpriseServiceId(state, { enterpriseId }) : null),
    categoryEnterpriseIdPropsExtender
);

export const getCategoryDefaultAgeCategoryOccupancy = createSelector(
    getUniqueAgeCategoriesPerClassification,
    getDefaultAdultCount,
    getDefaultChildCount,
    getCategoryMaxPersonCount,
    (
        { childAgeCategoryId, adultAgeCategoryId },
        baseAdultCount,
        baseChildCount,
        categoryMaxPersonCount
    ): option.Option<OccupancyData[]> => {
        if (baseAdultCount === undefined || baseChildCount === undefined) {
            return option.none;
        }

        let adultCount = pipe(baseAdultCount, ord.clamp(number.Ord)(0, categoryMaxPersonCount));
        const childCount = pipe(baseChildCount, ord.clamp(number.Ord)(0, categoryMaxPersonCount - adultCount));

        if (adultCount === 0 && childCount === 0) {
            adultCount = 1;
        }

        const occupancy: OccupancyData[] = [];
        if (adultAgeCategoryId != null) {
            occupancy.push({ ageCategoryId: adultAgeCategoryId, personCount: adultCount });
        }
        if (childAgeCategoryId != null) {
            occupancy.push({ ageCategoryId: childAgeCategoryId, personCount: childCount });
        }

        return array.isNonEmpty(occupancy) ? option.some(occupancy) : option.none;
    }
);

const getSelectedServiceCategoriesMeta = createSelector(
    getCategoriesMeta,
    getSelectedCityServiceIds,
    (metadata, serviceIds) =>
        pipe(
            serviceIds,
            array.map((serviceId) => record.lookup(serviceId, metadata)),
            array.compact
        )
);

export const areSelectedServiceCategoriesFetching = createSelector(
    getSelectedServiceCategoriesMeta,
    flow(array.some((meta) => meta.isFetching))
);

export const hasSelectedServiceCategoriesFailed = createSelector(
    getSelectedServiceCategoriesMeta,
    flow(array.some((meta) => meta.hasFailed))
);

const getSpaceCategoriesViewPreparation = createSelector(
    parentSelector,
    (state: ApplicationState) => state.views[SPACE_CATEGORIES_VIEW_SLICE_NAME]
);

export const isSpaceCategoriesViewPreparing = createSelector(
    getSpaceCategoriesViewPreparation,
    getOrFalse('isPreparing')
);

export const hasSpaceCategoriesViewFailed = createSelector(
    getSpaceCategoriesViewPreparation,
    getOrFalse('hasPreparationFailed')
);
